/**
 * MailboxCreateForm Component
 *
 * Presentational create-mailbox form (prefix + auto-domain, live preview, submit).
 * Reused by MailboxSettings and the document-list email-setup prompt modal.
 */
import { Button, Icon, Input, InputWrapper } from '@insio/ui-toolkit';
import type { useMailAddressSettings } from '../../../hooks/use-mail-address-settings';

type MailAddressSettings = ReturnType<typeof useMailAddressSettings>;

interface MailboxCreateFormProps {
  t: MailAddressSettings['t'];
  prefix: string;
  domain: string;
  previewAddress: string;
  displayError: string | null;
  handlePrefixChange: MailAddressSettings['handlePrefixChange'];
  handleCreate: () => void;
  creating: boolean;
  isBusy: boolean;
  /** Whether to render the "what this does" info hint above the input */
  showInfoHint?: boolean;
}

export function MailboxCreateForm({
  t,
  prefix,
  domain,
  previewAddress,
  displayError,
  handlePrefixChange,
  handleCreate,
  creating,
  isBusy,
  showInfoHint = true
}: MailboxCreateFormProps) {
  return (
    <div className='space-y-md'>
      {showInfoHint && (
        <div className='flex gap-sm rounded-lg bg-info-bg px-md py-sm'>
          <Icon name='circle-info' size='sm' className='text-info-text mt-2xs shrink-0' />
          <p className='text-sm text-info-text'>{t('hints.createInfo')}</p>
        </div>
      )}

      <InputWrapper
        label={t('fields.prefix')}
        htmlFor='mailbox-prefix'
        labelSize='sm'
        hint={!displayError ? t('fields.prefixHint') : undefined}
      >
        <div className='flex items-center gap-sm mb-sm'>
          <div className='flex-1'>
            <Input
              id='mailbox-prefix'
              value={prefix}
              onChange={handlePrefixChange}
              placeholder={t('placeholders.prefix')}
              type='text'
              autoComplete='off'
              hasError={!!displayError}
              leftContent={<Icon name='envelope' size='sm' />}
            />
          </div>
          <span className='text-sm text-foreground-muted font-mono whitespace-nowrap'>
            @{domain}
          </span>
        </div>
        {displayError && <p className='text-sm text-error-text'>{displayError}</p>}
      </InputWrapper>

      {/* Live preview */}
      {previewAddress && !displayError && (
        <div className='flex items-center gap-sm rounded-lg bg-background-muted px-md py-sm min-w-0'>
          <Icon name='eye' size='sm' className='text-foreground-muted shrink-0' />
          <span className='text-sm text-foreground-muted shrink-0'>{t('preview')}</span>
          <span className='text-sm font-mono font-medium text-foreground truncate min-w-0'>
            {previewAddress}
          </span>
        </div>
      )}

      {/* Actions */}
      <div className='flex justify-end'>
        <Button
          variant='primary'
          onClick={() => void handleCreate()}
          disabled={isBusy || !prefix.trim()}
          testId='mailbox-create'
        >
          {creating ? t('actions.creating') : t('actions.create')}
        </Button>
      </div>
    </div>
  );
}
